import React, { useEffect, useState, useMemo } from "react";
import {
  PieChart, Pie, Cell, Tooltip as PieTooltip, ResponsiveContainer as PieResponsive,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as BarTooltip, Legend as BarLegend, ResponsiveContainer,
} from "recharts";
import { Paper, Box, Typography, LinearProgress, IconButton, MenuItem, Select, useTheme, useMediaQuery } from "@mui/material";
import { parseISO, startOfDay, endOfDay, format } from "date-fns";
import axiosInstance from "../../../../lib/axios";

const STATUS = {
  O: { key: "unassigned", label: "Unassigned" },
  I: { key: "inProgress", label: "InProgress" },
  H: { key: "hold", label: "Hold" },
  R: { key: "resolved", label: "Resolved" },
  C: { key: "closed", label: "Closed" },
};

const WeeklyTicketsGraph = ({ globalProperty, selectedCategory, selectedWarden, fromDate, toDate }) => {
  const theme = useTheme();
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("weekly");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const colors = {
    unassigned: theme.palette.error.main,
    inProgress: "#FF9800",
    hold: "#8E24AA",
    resolved: "#FF5722",
    closed: "#4CAF50",
  };

  // Fetch tickets
  const fetchTickets = async () => {
    if (!globalProperty || !selectedCategory?.Id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const payload = {
        PageNumber: 1,
        PageSize: 9999,
        PropId: globalProperty,
        Id: selectedCategory.Id,
        WardenId: selectedWarden?.Id || 1,
        ServiceProvId: 1,
        SearchText: "",
        SearchFrom: fromDate,
        SearchTo: toDate,
        Flag: "TicketDash",
      };
      const { data: { Status, Data } } = await axiosInstance.post("TrnTicketing/GetFilterTicketDashBoard", payload);
      if (Status !== 0 || !Array.isArray(Data)) {
        setRaw([]);
        return;
      }
      const start = startOfDay(new Date(fromDate));
      const end = endOfDay(new Date(toDate));
      const filtered = Data.filter(t => {
        const d = parseISO(t.TktDate);
        return d >= start && d <= end;
      });
      setRaw(filtered);
    } catch (e) {
      console.error(e);
      setRaw([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [globalProperty, selectedCategory, selectedWarden, fromDate, toDate]);

  // Count tickets by status including hold
  const countsByStatus = useMemo(() => {
    const counts = { unassigned: 0, inProgress: 0, hold: 0, resolved: 0, closed: 0 };
    raw.forEach(t => {
      const stKey = (t.TicketSt || "O").toUpperCase();
      if (stKey === "H") {
        counts.hold++;
      } else {
        const status = STATUS[stKey] || STATUS.O;
        counts[status.key] = (counts[status.key] || 0) + 1;
      }
    });
    return counts;
  }, [raw]);

  // Prepare donut data with new hold and colors
  const donutData = [
    { name: "Unassigned", value: countsByStatus.unassigned, color: colors.unassigned },
    { name: "InProgress", value: countsByStatus.inProgress, color: colors.inProgress },
    { name: "Hold", value: countsByStatus.hold, color: colors.hold },
    { name: "Resolved", value: countsByStatus.resolved, color: colors.resolved },
    { name: "Closed", value: countsByStatus.closed, color: colors.closed },
  ];

  const totalSold = donutData.reduce((acc, d) => acc + d.value, 0);

  // Bar chart data grouped by day and status keys
  const barData = useMemo(() => {
    const groups = {};
    raw.forEach(t => {
      const day = format(parseISO(t.TktDate), "EEE");
      if (!groups[day]) groups[day] = { day, unassigned: 0, inProgress: 0, hold: 0, resolved: 0, closed: 0 };
      const stKey = (t.TicketSt || "O").toUpperCase();
      if (stKey === "H") {
        groups[day].hold++;
      } else {
        const status = STATUS[stKey] || STATUS.O;
        groups[day][status.key]++;
      }
    });
    const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    return weekDays.map(day => groups[day] || { day, unassigned: 0, inProgress: 0, hold: 0, resolved: 0, closed: 0 });
  }, [raw]);
  const tooltipStyle = { color: "#4caf50", fontWeight: "bold" };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: {
        xs: "column",
        md: "row",
      },
      gap: 1,
    }}>
      {/* Donut Chart */}
      <Box sx={{
        flex: isMobile ? "0 0 220px" : "1 1 40%",
        width: "100%",
        minWidth: 0,
        minHeight: { xs: 200, sm: 280, md: 300 },
        position: "relative",
      }}>
        <PieResponsive width="100%" height="100%">
          <PieChart>
            <Pie
              data={donutData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? 40 : 60}
              outerRadius={isMobile ? 60 : 100}
              paddingAngle={5}
              labelLine={false}
              label={({ name, percent }) => isMobile ? `${name} ${((percent || 0) * 100).toFixed(0)}%` : `${name} ${(percent * 100).toFixed(0)}%`}
            >
              {donutData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <PieTooltip formatter={(value) => `${value} tickets`} />
          </PieChart>
        </PieResponsive>
        {/* Centered total count inside the donut */}
        <Typography
          variant="h5"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontWeight: "bold",
            pointerEvents: "none",
            userSelect: "none",
            fontSize: {
              xs: "12px",
              sm: "16px",
              md: "20px",
            },
          }}
        >
          {totalSold} Tickets
        </Typography>
      </Box>
      {/* Bar Chart */}
      <Paper sx={{
        flex: { xs: "1 1 100%", md: "1 1 60%" },
        width: "100%",
        minWidth: 0,
        p: 3,
        borderRadius: 3,
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Weekly Tickets Analytics</Typography>
          <Select
            size="small"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="weekly">Weekly</MenuItem>
          </Select>
        </Box>
        <Box sx={{
          height: { xs: 180, md: 250 },
          minHeight: 220,
          // overflowX: isMobile ? "auto" : "visible",
          // width: "100%",
          // minWidth: isMobile ? 400 : "100%",
          // height: { xs: 180, md: 250 },
          // minHeight: 100,
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} margin={{ top: 20, right: 20, left: -20 }} barGap={1} barCategoryGap="10%">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" />
              <YAxis />
              <BarTooltip
                formatter={(value) => <span style={tooltipStyle}>{value} tickets</span>}
              />
              <BarLegend />
              <Bar dataKey="unassigned" name="Unassigned" fill={colors.unassigned} radius={[6, 6, 0, 0]} />
              <Bar dataKey="inProgress" name="InProgress" fill={colors.inProgress} radius={[6, 6, 0, 0]} />
              <Bar dataKey="hold" name="Hold" fill={colors.hold} radius={[6, 6, 0, 0]} />
              <Bar dataKey="resolved" name="Resolved" fill={colors.resolved} radius={[6, 6, 0, 0]} />
              <Bar dataKey="closed" name="Closed" fill={colors.closed} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
};
export default WeeklyTicketsGraph;
